import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { getPodApiBaseUrl } from '@/config/apiBaseUrl'
import { API_TIMEOUT_MS } from '@/api/timeouts'
import {
  getFingerprint,
  getIamAccessToken,
  getSessionId,
  setFingerprint,
  setIamAccessToken,
  clearIamAccessToken,
  clearAuthCredentials,
  clearLegacyMockTokens,
  clearPendingIamMfaToken,
  getPendingIamMfaToken,
  setPendingIamMfaToken,
  FP_KEY,
  IAM_JWT_KEY,
  IAM_SESSION_ID_KEY,
  setSessionId,
} from '@/api/authSession'

clearLegacyMockTokens()

/** Re-export session helpers for services / stores (suite-compatible names). */
export {
  getFingerprint,
  setFingerprint,
  getIamAccessToken,
  setIamAccessToken,
  clearIamAccessToken,
  getSessionId,
  setSessionId,
  clearAuthCredentials,
  clearLegacyMockTokens,
  clearPendingIamMfaToken,
  getPendingIamMfaToken,
  setPendingIamMfaToken,
  FP_KEY,
  IAM_JWT_KEY,
  IAM_SESSION_ID_KEY,
}

/** Full logout local storage: credentials + legacy keys. */
export function clearFingerprint(): void {
  clearAuthCredentials()
  clearLegacyMockTokens()
}

const apiClient: AxiosInstance = axios.create({
  baseURL: getPodApiBaseUrl(),
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const fp = getFingerprint()
    if (fp) config.headers['X-XSRF-Token'] = fp

    const jwt = getIamAccessToken()
    if (jwt) config.headers.Authorization = `Bearer ${jwt}`

    const sid = getSessionId()
    if (sid) config.headers['X-Session-Id'] = sid

    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers as Record<string, unknown> & { delete?: (name: string) => void }
      if (typeof h.delete === 'function') {
        h.delete('Content-Type')
      } else {
        delete h['Content-Type']
        delete h['content-type']
      }
    }
    return config
  },
  (error: AxiosError) => Promise.reject(error)
)

function isPublicAuthPath(url?: string): boolean {
  if (!url) return false
  return (
    url.includes('/api/public/') ||
    url.endsWith('/health') ||
    url.includes('/api/docs')
  )
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const reqUrl = error.config?.url || ''
    const path = window.location.pathname || ''
    const onLoginScreen = path.startsWith('/login')

    if (error.response?.status === 401 && !isPublicAuthPath(reqUrl)) {
      // Do not wipe a fresh OTP session when an older validateSession /me call fails on /login.
      if (!onLoginScreen) {
        clearFingerprint()
        window.location.href = '/login'
      }
    } else if (error.response?.status === 403) {
      console.error('Access denied: Insufficient permissions')
    } else if (error.response?.status === 429) {
      console.error('Rate limit exceeded')
    } else if (error.response && error.response.status >= 500) {
      console.error('Server error occurred')
    }

    const data = error.response?.data as Record<string, unknown> | undefined
    const bodyText =
      (typeof data?.message === 'string' && data.message) ||
      (typeof data?.error === 'string' && data.error) ||
      (Array.isArray(data?.errors) && data.errors.map(String).join('; ')) ||
      ''

    const apiError: { message: string; code?: string; statusCode?: number } = {
      message: bodyText || error.message || 'An unexpected error occurred',
      code: error.code,
      statusCode: error.response?.status,
    }

    return Promise.reject(apiError)
  }
)

export default apiClient
export { apiClient }
